<template>
    <div>
        <v-row class="justify-center">
            <v-col cols="6" class="points">
                <points-patch
                    :type="activityType"
                    :start-date="`${getFormattedDate(startDate, 'YYYY-MM-DD')} ${getFormattedTime(startDate, 'HH:MM')}`"
                    :end-date="`${getFormattedDate(endDate, 'YYYY-MM-DD')} ${getFormattedTime(endDate, 'HH:MM')}`"
                    :rhythm-repeat="rhythm"
                    :quest="quest"
                />
            </v-col>
        </v-row>

        <p :class="activityType + '--text'" class="font-weight-bold">
            {{ $t('activity-type-' + activityType) }}
        </p>

        <p class="text-h2 font-weight-bold">{{quest.name}}</p>

        <v-img :src="imgUrl" aspect-ratio="1"/>

        <p v-html="quest.description"></p>

        <div class="mb-3">
            <div class="text-h5 mb-0 font-weight-bold text-uppercase">{{$t('Art')}}</div>
            <div>{{ $t('activity-type-' + activityType) }}</div>
        </div>

        <div class="mb-3">
            <div class="text-h5 font-weight-bold text-uppercase">{{$t('Wann?')}}</div>
            <div>
                {{ $t('Start') }}: {{ getFormattedDate(startDate) }}
                <template v-if="!rhythmTitle"> - {{ getFormattedTime(startDate) }}</template>
            </div>
            <div>
                {{ $t('End') }}: {{ getFormattedDate(endDate) }}
                <template v-if="!rhythmTitle"> - {{ getFormattedTime(endDate) }}</template>
            </div>
            <div v-if="rhythmTitle">
                <span>{{$t(rhythmTitle)}}</span>
                <span v-if="selectedDays.length">: </span>
                <span v-if="rhythmType === 'monthly'">{{ startDateMessage }}</span>
                <template v-else>
                    <div v-for="(selectedDay, index) of selectedDays" :key="selectedDay.day" :class="index+1 < selectedDays.length ? 'mb-0' : ''">
                        {{ selectedDay.day }}. {{ selectedDay.startTime }} - {{ selectedDay.endTime }}
                    </div>
                </template>
            </div>
        </div>

        <div class="mb-3">
            <div class="text-h5 font-weight-bold text-uppercase">{{$t('Age')}}</div>
            <div v-if="!quest.ageFrom && !quest.ageTo">{{ $t('No age limit') }}</div>
            <div v-if="quest.ageFrom">{{$t('Minimum age')}}: {{quest.ageFrom}} {{$t('years')}}</div>
            <div v-if="quest.ageTo">{{$t('Maximum age')}}: {{quest.ageTo}} {{$t('years')}}</div>
        </div>

        <div class="mb-3">
            <div class="text-h5 font-weight-bold text-uppercase">{{$t('Points/Units')}}</div>
            <quest-calculated-points
                :start-date="pointsStartDate"
                :end-date="pointsEndDate"
                :is-responsible="quest.isResponsible"
                :rhythm-repeat="rhythm"
                :quest="quest"
            />
        </div>

        <div class="mb-3">
            <div class="text-h5 mb-0 font-weight-bold text-uppercase">{{$t('Xperiences')}}</div>
            <div>{{experiences.join(', ')}}</div>
        </div>

        <div v-if="quest.placeName" class="mb-3">
            <div class="text-h5 mb-0 font-weight-bold text-uppercase">{{$t('Wo?')}}</div>
            <div>
                {{quest.placeName}}
                <br>
                {{quest.street}}
                <br>
                {{quest.postCode}} {{quest.location}}
            </div>
            <div>
                {{ $t('Additional description of the location:') }}<br />
                {{ quest.locationAdditions }}
            </div>
        </div>

        <div>
            <div class="text-h5 mb-0 font-weight-bold text-uppercase">{{$t('Kontakt')}}</div>
            <div v-if="quest.useOrganizationData && organizationData">
                {{ organizationData.name }} <br>
                {{ organizationData.phone }} <br>
                {{ organizationData.email }}
            </div>

            <div v-else-if="quest.responsibleActivities && quest.responsibleActivities.length">
                {{ quest.responsibleActivities[0].firstname }} {{ quest.responsibleActivities[0].lastname }} <br>
                {{ quest.responsibleActivities[0].phone }} <br>
                {{ quest.responsibleActivities[0].email }}
            </div>
        </div>
    </div>
</template>

<script>
import PointsPatch from '../SubComponents/PointsPatch';
import QuestCalculatedPoints from '../QuestCalculatedPoints';
import moment from 'moment';
import {mapState} from 'vuex';

export default {
    components: {
        PointsPatch,
        QuestCalculatedPoints,
    },
    props: {
        quest: {
            type: Object,
        },
        selectedDays: {
            type: Array,
            default: () => {
                return [];
            },
        },
        experiences: {
            type: Array,
            default: () => {
                return [];
            },
        },
        rhythmTitle: {
            type: String,
        },
        rhythmType: {
            type: String,
        },
        galleryPath: {
            type: String,
        },
    },
    computed: {
        ...mapState('organization', [
            'selectedOrganizationID',
            'organizations',
        ]),
        organizationName() {
            let selectedOrganization = this.organizations.filter(o => o.id === this.selectedOrganizationID);

            return selectedOrganization.length ? selectedOrganization[0].name : null;
        },
        activityType() {
            if (typeof this.quest.activityType === 'string') {
                return this.quest.activityType;
            } else {
                return this.quest.activityType.indexname;
            }
        },
        pointsStartDate() {
            if (this.startDate) {
                return `${this.getFormattedDate(this.startDate, 'YYYY-MM-DD')} ${this.getFormattedTime(this.startDate, 'HH:MM')}`;
            }
        },
        pointsEndDate() {
            if (this.endDate) {
                return `${this.getFormattedDate(this.endDate, 'YYYY-MM-DD')} ${this.getFormattedTime(this.endDate, 'HH:MM')}`;
            }
        },
        startDate() {
            const date = this.quest.startDate;
            return date && date.date ? date.date : date;
        },
        endDate() {
            const date = this.quest.endDate;
            return date && date.date ? date.date : date;
        },
        startTime() {
            return moment(this.startDate).format('HH:mm');
        },
        endTime() {
            return moment(this.endDate).format('HH:mm');
        },
        startDateMessage() {
            if (this.selectedDays[0]) {
                const startDate = this.selectedDays[0].date;

                const weekNumber = 1 + Math.floor((startDate.date() - 1) / 7);

                const weekName = weekNumber === 5 ? this.$t('last') : `${weekNumber}.`;
                const dayName = startDate.format('dddd');

                return `${this.$t('every')} ${weekName} ${dayName} ${this.$t('of a month')}`;
            }
        },
        rhythm() {
            if (this.quest.rhytm) {
                if (typeof this.quest.rhytm === 'object') {
                    return this.quest.rhytm.id;
                } else {
                    return this.quest.rhytm;
                }
            }
            return null;
        },
        imgUrl() {
            if (this.galleryPath) {
                return `${this.$explorer.getBaseUrl()}/${this.galleryPath}/${this.quest.logo}`;
            } else {
                return this.quest.logo;
            }
        },
        organizationData() {
            return this.organizations.find(o => o.id === this.selectedOrganizationID);
        },
    },
    methods: {
        getFormattedDate(date, format = 'DD.MM.YY') {
            if (!this.quest.id) {
                date = date.replaceAll('Z', '');
            }
            return moment(date).format(format);
        },
        getFormattedTime(date, format = 'HH:mm') {
            if (!this.quest.id) {
                date = date.replaceAll('Z', '');
            }
            return moment(date).format(format);
        },
    },
};
</script>
